import { createFileRoute } from "@tanstack/react-router";
import React, { useEffect, useState } from "react";
import { getERPSummary, getERPProjects, updateWorkOrderStatus } from "../api";

export const Route = createFileRoute("/ErpProject")({
  component: ErpProject,
});

interface ERPSummary {
  totalProjects: number;
  delivered: number;
  inProduction: number;
  design: number;
  totalCost: number;
  totalWorkOrders: number;
  completedWorkOrders: number;
  completionPercent: number;
  materialShortages: any[];
}

export function ErpProject() {
  const [summary, setSummary] = useState<ERPSummary | null>(null);
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [sData, pData] = await Promise.all([
        getERPSummary(),
        getERPProjects(),
      ]);
      setSummary(sData);
      setProjects(pData);
    } catch (err) {
      console.error("ERP Data Load Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      body, html, #app { 
        margin: 0; padding: 0; width: 100%; height: 100%; 
        overflow: hidden; background-color: #000;
      }
      ::-webkit-scrollbar { width: 4px; }
      ::-webkit-scrollbar-track { background: #050505; }
      ::-webkit-scrollbar-thumb { background: #222; }
    `;
    document.head.appendChild(style);
    fetchData();
    return () => { document.head.removeChild(style); };
  }, []);

  const handleStatusUpdate = async (id: number, newStatus: string) => {
    await updateWorkOrderStatus(id, newStatus);
    fetchData();
  };

  if (loading)
    return <div style={loadingStyle}>INITIALIZING_ERP_CORE...</div>;

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <div>
          <h1 style={{ margin: 0, letterSpacing: "2px", fontSize: '18px' }}>ERP_CORE_v1.0.4</h1>
          <p style={{ color: "#444", fontSize: "10px", margin: "5px 0 0 0" }}>
            NODE: {window.location.hostname.toUpperCase()} // STATUS: ACTIVE
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <span style={labelStyle}>SYSTEM_TIME</span>
          <div style={{ fontSize: '14px', fontFamily: 'monospace' }}>{new Date().toLocaleTimeString()}</div>
        </div>
      </header>

      <div style={mainGridStyle}>
        {/* Top Row: Cards */}
        <div style={statsRowStyle}>
          <div style={cardStyle}>
            <span style={labelStyle}>TOTAL_PROJECTS</span>
            <div style={valueStyle}>{summary?.totalProjects}</div>
            <div style={subValueStyle}>{summary?.inProduction} ACTIVE / {summary?.design} PLANNED</div>
          </div>
          <div style={cardStyle}>
            <span style={labelStyle}>WORK_ORDERS</span>
            <div style={valueStyle}>{summary?.totalWorkOrders}</div>
            <div style={subValueStyle}>{summary?.completedWorkOrders} EXECUTED</div>
          </div>
          <div style={cardStyle}>
            <span style={labelStyle}>CAPITAL_EXPENDITURE</span>
            <div style={valueStyle}>${summary?.totalCost.toLocaleString()}</div>
            <div style={subValueStyle}>LIVE_MARKET_VALUATION</div>
          </div>
          <div style={cardStyle}>
            <span style={labelStyle}>CORE_EFFICIENCY</span>
            <div style={{ ...valueStyle, color: "#4da3ff" }}>{summary?.completionPercent}%</div>
            <div style={subValueStyle}>AGGREGATE_OUTPUT</div>
          </div>
        </div>

        {/* Middle Row: Split View */}
        <div style={contentSplitStyle}>
          <div style={sectionStyle}>
            <h3 style={labelStyle}>PROJECT_REGISTRY</h3>
            <div style={tableWrapperStyle}>
              <table style={tableStyle}>
                <thead>
                  <tr style={tableHeaderRowStyle}>
                    <th style={thStyle}>ID</th>
                    <th style={thStyle}>CLIENT_ID</th>
                    <th style={thStyle}>VALUATION</th>
                    <th style={thStyle}>STATUS</th>
                    <th style={thStyle}>CMD</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p) => (
                    <tr key={p.id} style={tableRowStyle}>
                      <td style={tdStyle}>{p.id}</td>
                      <td style={tdStyle}>{p.customer}</td>
                      <td style={tdStyle}>${p.cost.toLocaleString()}</td>
                      <td style={{ ...tdStyle, color: p.status === "Delivered" ? "#00ff00" : "#ffcc00" }}>
                        {p.status.toUpperCase()}
                      </td>
                      <td style={tdStyle}>
                        <button onClick={() => handleStatusUpdate(p.id, "In Progress")} style={actionBtnStyle}>
                          EXEC
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={sectionStyle}>
            <h3 style={labelStyle}>LINEAR_PRODUCTION_TRACKING</h3>
            <div style={chartContainer}>
              {projects.map((p) => (
                <div key={p.id} style={{ marginBottom: "25px" }}>
                  <div style={progressLabelStyle}>
                    <span>
                      <span style={{ color: p.status === "Delivered" ? "#00ff00" : "#444", marginRight: "10px" }}>●</span>
                      {p.customer}
                    </span>
                    <span style={{ color: "#555" }}>
                       {p.status === "Delivered" ? "100%" : p.status === "In Production" ? "50%" : "15%"}
                    </span>
                  </div>
                  <div style={barBg}>
                    <div style={{ 
                      ...barFill, 
                      width: p.status === "Delivered" ? "100%" : p.status === "In Production" ? "50%" : "15%",
                      backgroundColor: p.status === "Delivered" ? "#00ff00" : "#4da3ff"
                    }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Styles ---

const containerStyle: React.CSSProperties = {
  backgroundColor: "#000", color: "#fff",
  height: "100vh", width: "100vw",
  display: "flex", flexDirection: "column",
  fontFamily: "'Courier New', monospace", boxSizing: "border-box",
};

const headerStyle: React.CSSProperties = {
  display: "flex", justifyContent: "space-between", alignItems: "center",
  padding: "15px 30px", borderBottom: "1px solid #1a1a1a",
  background: "#050505",
};

const mainGridStyle: React.CSSProperties = {
  flex: 1, padding: "20px 30px",
  display: "flex", flexDirection: "column", gap: "20px",
  overflow: "hidden",
};

const statsRowStyle: React.CSSProperties = {
  display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "15px",
};

const contentSplitStyle: React.CSSProperties = {
  display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: "20px", flex: 1, overflow: "hidden",
};

const cardStyle: React.CSSProperties = {
  backgroundColor: "#050505", border: "1px solid #1a1a1a", padding: "15px",
};

const sectionStyle: React.CSSProperties = {
  backgroundColor: "#050505", border: "1px solid #1a1a1a",
  padding: "20px", display: "flex", flexDirection: "column", overflow: "hidden",
};

const labelStyle: React.CSSProperties = {
  color: "#333", fontSize: "9px", fontWeight: "bold",
  letterSpacing: "2px", marginBottom: "10px", display: "block",
};

const valueStyle: React.CSSProperties = { fontSize: "24px", fontWeight: "bold" };
const subValueStyle: React.CSSProperties = { fontSize: "9px", color: "#222", marginTop: "4px" };

const tableWrapperStyle: React.CSSProperties = { flex: 1, overflowY: "auto" };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse" };
const tableHeaderRowStyle: React.CSSProperties = { textAlign: "left", borderBottom: "1px solid #1a1a1a" };
const tableRowStyle: React.CSSProperties = { borderBottom: "1px solid #0a0a0a" };
const thStyle: React.CSSProperties = { padding: "12px 8px", fontSize: "9px", color: "#222" };
const tdStyle: React.CSSProperties = { padding: "12px 8px", fontSize: "12px", borderBottom: "1px solid #0a0a0a" };

const chartContainer: React.CSSProperties = {
  flex: 1, display: "flex", flexDirection: "column",
  padding: "10px 5px", overflowY: "auto",
  backgroundImage: "linear-gradient(rgba(18, 16, 16, 0) 50%, rgba(0, 0, 0, 0.1) 50%), linear-gradient(90deg, rgba(255, 0, 0, 0.03), rgba(0, 255, 0, 0.01), rgba(0, 0, 255, 0.03))",
  backgroundSize: "100% 3px, 3px 100%",
};

const progressLabelStyle: React.CSSProperties = {
  display: "flex", justifyContent: "space-between", alignItems: "flex-end",
  fontSize: "11px", marginBottom: "8px", letterSpacing: "1px", textTransform: "uppercase", color: "#888",
};

const barBg: React.CSSProperties = { 
  height: "6px", backgroundColor: "#111", border: "1px solid #222", position: "relative", overflow: "hidden" 
};

const barFill: React.CSSProperties = { 
  height: "100%", backgroundColor: "#4da3ff", boxShadow: "0 0 8px #4da3ff44", transition: "width 1.2s ease" 
};

const actionBtnStyle: React.CSSProperties = {
  backgroundColor: "transparent", color: "#fff", border: "1px solid #333",
  padding: "4px 10px", fontSize: "9px", cursor: "pointer",
};

const loadingStyle: React.CSSProperties = {
  height: "100vh", width: "100vw", backgroundColor: "#000", color: "#4da3ff",
  display: "flex", justifyContent: "center", alignItems: "center", fontFamily: "monospace",
};

export default ErpProject;